import { useEffect, useState } from "react";
import { UserCheck, Users, Search, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

const BLANK = { name: "", id_number: "", email: "" };

/**
 * Signatar(er) på vegne av en selskaps-part. Et selskap kan ikke signere selv med BankID,
 * så en fysisk person med fødselsnummer må oppnevnes. Krever vedtektene det, kan to personer
 * legges inn – begge må da signere «i fellesskap». Finnes personen i systemet, havner
 * signeringen under «Venter på din signatur»; ellers sendes BankID-lenke på e-post.
 *
 * value: array av { name, id_number, email } (1 eller 2). onChange(newArray).
 * orgNumber (valgfri): org.nr for «Hent fra Proff»-oppslag (signaturrett + navn).
 * onProffName (valgfri): kalles med selskapsnavnet fra Proff (brukes i «Ny bedrift»-flyt).
 */
export function SignatoryFields({ companyName, value, onChange, testIdPrefix, orgNumber, onProffName, onVerified }) {
  const sigs = value && value.length ? value : [BLANK];
  const joint = sigs.length > 1;
  const [busy, setBusy] = useState(false);
  const [proffNote, setProffNote] = useState("");
  const [verified, setVerified] = useState(false);

  const setSig = (i, field, val) => onChange(sigs.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));
  const toggleJoint = (on) => onChange(on ? [sigs[0] || BLANK, BLANK] : [sigs[0] || BLANK]);

  const cleanOrg = String(orgNumber || "").replace(/\D/g, "");
  useEffect(() => {
    setVerified(false);
    setProffNote("");
  }, [cleanOrg]);

  const fetchProff = async () => {
    if (cleanOrg.length !== 9) {
      toast.error("Skriv inn et gyldig org.nr (9 siffer) først");
      return;
    }
    setBusy(true);
    setProffNote("");
    try {
      const { data } = await api.get(`/proff/company/${cleanOrg}`);
      if (onProffName && data.name) onProffName(data.name);
      const sig = data.signature || {};
      const names = (sig.signatories || []).slice(0, 2);
      if (names.length) {
        onChange(names.map((n) => ({ name: n, id_number: "", email: "" })));
      }
      setProffNote(
        sig.text ||
          (names.length
            ? `Hentet fra Proff${sig.joint ? " – signeres i fellesskap" : ""}`
            : "Fant ikke signaturrett i Proff – fyll inn manuelt")
      );
      setVerified(true);
      if (onVerified) onVerified(data.name || "");
      toast.success(`Hentet ${data.name || "selskap"} fra Proff`);
    } catch (e) {
      setVerified(false);
      toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke hente fra Proff");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3" data-testid={`${testIdPrefix}-block`}>
      <div className="flex items-center justify-between gap-2">
        <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
          <UserCheck className="h-3.5 w-3.5" /> Signerer på vegne av {companyName || "selskapet"}
          {verified ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300" data-testid={`${testIdPrefix}-verified`}>
              <CheckCircle2 className="h-3 w-3" /> Bekreftet mot Proff
            </span>
          ) : null}
        </p>
        {orgNumber !== undefined ? (
          <Button type="button" size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={fetchProff}
            disabled={busy} data-testid={`${testIdPrefix}-proff-btn`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Hent fra Proff
          </Button>
        ) : null}
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Et selskap kan ikke signere selv. Legg inn personen som signerer på vegne av selskapet – navn og e-post.
        Fødselsnummer er valgfritt (fanges automatisk fra BankID ved signering). Finnes personen i systemet, får de
        oppgaven i appen; ellers sendes en BankID-lenke på e-post.
      </p>
      {proffNote ? (
        <p className="mt-1 rounded-md bg-background/60 px-2 py-1 text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-proff-note`}>
          <span className="font-semibold">Proff:</span> {proffNote}.
        </p>
      ) : null}

      <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] font-medium text-amber-800 dark:text-amber-200">
        <Checkbox checked={joint} onCheckedChange={toggleJoint} data-testid={`${testIdPrefix}-joint-toggle`} />
        <Users className="h-3.5 w-3.5" /> Krever signering i fellesskap (to personer)
      </label>

      {sigs.map((s, i) => (
        <div key={i} className="mt-2">
          {joint ? <p className="text-[11px] font-semibold text-muted-foreground">Signatar {i + 1}</p> : null}
          <div className="mt-1 grid grid-cols-2 gap-2.5">
            <div className="col-span-2">
              <Label className="text-xs">Navn på signatar</Label>
              <Input className="mt-1" data-testid={`${testIdPrefix}${i === 0 ? "" : `-${i + 1}`}-name`} value={s.name || ""}
                onChange={(e) => setSig(i, "name", e.target.value)} placeholder="Fornavn Etternavn" />
            </div>
            <div>
              <Label className="text-xs">E-post (for BankID)</Label>
              <Input className="mt-1" data-testid={`${testIdPrefix}${i === 0 ? "" : `-${i + 1}`}-email`} value={s.email || ""}
                onChange={(e) => setSig(i, "email", e.target.value)} placeholder="navn@firma.no" />
            </div>
            <div>
              <Label className="text-xs">Fødselsnummer (valgfritt)</Label>
              <Input className="mt-1" data-testid={`${testIdPrefix}${i === 0 ? "" : `-${i + 1}`}-idnr`} value={s.id_number || ""}
                onChange={(e) => setSig(i, "id_number", e.target.value)} placeholder="11 siffer" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Valider en signatar-liste for en selskaps-part. Krever navn + e-post (fnr valgfritt). Returnerer feilmelding (streng) eller "". */
export function validateSignatories(list, companyName) {
  const sigs = (list && list.length ? list : []).filter(Boolean);
  if (!sigs.length || !sigs[0].name?.trim() || !sigs[0].email?.trim()) {
    return `Legg inn signatar (navn + e-post) på vegne av ${companyName || "selskapet"}`;
  }
  for (const s of sigs) {
    if (!s.name?.trim() || !s.email?.trim()) {
      return `Fyll inn navn og e-post for alle signatarer på vegne av ${companyName || "selskapet"}`;
    }
    const nid = (s.id_number || "").replace(/\D/g, "");
    if (s.id_number?.trim() && nid.length !== 11) {
      return `Fødselsnummeret til ${s.name} må ha 11 siffer, eller la det stå tomt`;
    }
  }
  return "";
}
